/**
 * A wrapper around console functions that works in non-prod envs
 */
export enum LogTypes {
  Log = 'log',
  Info = 'info',
  Warn = 'warn',
  Error = 'error',
  Debug = 'debug',
}

export class AppLogger {
  private isDebugMode: boolean = false;
  // the environment variables of the app that imports this module will be used
  public isProduction: boolean = true;

  private enabledFeatures: { [key: string]: boolean } = {};

  // Only logs if isDebugMode is true
  private callLog(method: LogTypes, ...messages: any[]) {
    if (this.isDebugMode) {
      if (messages.length > 0) {
        messages[0] = `[APPLOGGER] ${messages[0]}`;
      }
      console[method](...messages);
    }
  }

  private static instance: AppLogger;

  configure(
    { isProduction }: { isProduction: boolean } = {
      isProduction: true,
    },
  ) {
    this.isProduction = isProduction;
    let debugLogsOnValue = 'false';

    // If we try to access localStorage in a non-browser environment, it will throw an error (Next.js)
    // Instead let's check the type of window object
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      debugLogsOnValue = localStorage.getItem('debugLogsOn') || '';
    }
    this.isDebugMode = debugLogsOnValue === 'true' && !this.isProduction;
  }

  log(...messages: any[]) {
    this.callLog(LogTypes.Log, ...messages);
  }

  info(...messages: any[]) {
    this.callLog(LogTypes.Info, ...messages);
  }

  warn(...messages: any[]) {
    this.callLog(LogTypes.Warn, ...messages);
  }

  error(...messages: any[]) {
    this.callLog(LogTypes.Error, ...messages);
  }

  debug(...messages: any[]) {
    this.callLog(LogTypes.Debug, ...messages);
  }

  // This type of log controls logging of a specific feature
  // which can be enabled or disabled by the setEnabledFeatures method
  logFeature(feature: string, ...messages: any[]) {
    if (!this.enabledFeatures[feature]) {
      return;
    }
    this.callLog(LogTypes.Log, `[${feature}]`, ...messages);
  }

  public setEnabledFeatures(features: { [key: string]: boolean }) {
    this.enabledFeatures = features;
  }

  public static getInstance(): AppLogger {
    if (!AppLogger.instance) {
      AppLogger.instance = new AppLogger();
    }
    return AppLogger.instance;
  }
}

// Singleton pattern - only one instance of AppLogger can exist
export const appLogger = AppLogger.getInstance();
